
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/store';

export type CopilotTab = 'chat' | 'history' | 'settings';

interface CopilotState {
  isOpen: boolean;
  isCollapsed: boolean;
  activeTab: CopilotTab;
}

// Initialize from localStorage if available
const getInitialState = (): CopilotState => {
  try {
    const savedState = localStorage.getItem('copilotUIState');
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (e) {
    console.error('Failed to load copilot UI state from localStorage:', e);
  }
  
  return {
    isOpen: false,
    isCollapsed: false,
    activeTab: 'chat',
  };
};



const initialState: CopilotState = getInitialState();

// Function to save state to localStorage
const saveState = (state: CopilotState) => {
  try {
    localStorage.setItem('copilotUIState', JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save copilot UI state to localStorage:', e);
  }
};


export const copilotSlice = createSlice({
  name: 'copilot',
  initialState,
  reducers: {
    openDrawer: (state) => {
      // Make sure to set both isOpen to true and isCollapsed to false
      state.isOpen = true;
      state.isCollapsed = false;
      saveState(state);
    },
    closeDrawer: (state) => {
      state.isOpen = false;
      saveState(state);
    },
    toggleDrawer: (state) => {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.isCollapsed = false;
      }
      saveState(state);
    },
    collapseDrawer: (state) => {
      state.isCollapsed = true;
      saveState(state);
    },
    expandDrawer: (state) => {
      state.isCollapsed = false;
      saveState(state);
    },
    setActiveTab: (state, action: PayloadAction<CopilotTab>) => {
      state.activeTab = action.payload;
      saveState(state);
    },
    startNewConversation: (state) => {
      state.isOpen = true;
      state.isCollapsed = false;
      state.activeTab = 'chat';
      saveState(state);
    },
  },
});

export const {
  openDrawer,
  closeDrawer,
  toggleDrawer,
  collapseDrawer,
  expandDrawer,
  setActiveTab,
  startNewConversation,
} = copilotSlice.actions;

export const selectCopilotState = (state: RootState) => state.copilot;

export default copilotSlice.reducer;
